import { JSONParser } from '@streamparser/json';
import { environment } from 'src/environments/environment';
import { validateWgs84GeoJsonDocument } from 'src/app/core/util/wgs84-geojson';

/** Allowed geometry types for GeoJSON import (must match backend allowlist). */
export const GEOJSON_ALLOWED_GEOMETRY_TYPES = [
  'Point',
  'LineString',
  'Polygon',
  'MultiPoint',
  'MultiLineString',
  'MultiPolygon',
] as const;

export type GeoJsonAllowedGeometryType = (typeof GEOJSON_ALLOWED_GEOMETRY_TYPES)[number];

/** Fallback when environment.geojsonPreviewFeatureCap is missing, non-numeric, or ≤0. */
export const GEOJSON_PREVIEW_FEATURE_CAP = 5000;

export function resolveGeoJsonPreviewFeatureCap(raw: unknown = (environment as { geojsonPreviewFeatureCap?: unknown }).geojsonPreviewFeatureCap): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return GEOJSON_PREVIEW_FEATURE_CAP;
  }
  return Math.floor(n);
}

export function isAllowedGeoJsonGeometryType(type: unknown): type is GeoJsonAllowedGeometryType {
  return typeof type === 'string' && (GEOJSON_ALLOWED_GEOMETRY_TYPES as readonly string[]).includes(type);
}

export class GeoJsonStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeoJsonStreamError';
  }
}

export type StreamGeoJsonResult = {
  featuresForPreview: any[];
  /** Features with a non-empty dialog_stakeholder_name, collected across the whole file (capped). */
  stakeholderFeaturesForPreview: any[];
  totalFeatures: number;
  /** Non-empty dialog_stakeholder_name on allowed geometries (full file, not capped). */
  totalStakeholderFeatures: number;
  /** Features skipped as unsupported (GeometryCollection / unknown / missing). */
  skippedUnsupportedFeatures: number;
  capped: boolean;
  /** Set when root CRS or coordinates fail WGS84 rules after streaming to EOF. */
  wgs84Error: string | null;
};

function applyWgs84Slice(
  wgs84Error: string | null,
  slice: Parameters<typeof validateWgs84GeoJsonDocument>[0]
): string | null {
  if (wgs84Error) {
    return wgs84Error;
  }
  return validateWgs84GeoJsonDocument(slice);
}

function hasPreviewStakeholderName(feature: any): boolean {
  const name = feature?.properties?.dialog_stakeholder_name;
  if (name == null) {
    return false;
  }
  return String(name).trim() !== '';
}

/**
 * Incrementally parse a GeoJSON FeatureCollection from a File via ReadableStream.
 * Skips GeometryCollection / unknown / missing geometry; includes Multi*. Keeps at most previewCap
 * allowed features for the map.
 *
 * Uses `@streamparser/json` with path-based extraction of root `crs` and `features[]`; the stream
 * MUST read through EOF (no early cancel) for WGS84 and crs-at-end support.
 */
export async function streamGeoJsonFeatures(file: File): Promise<StreamGeoJsonResult> {
  const previewCap = resolveGeoJsonPreviewFeatureCap();
  const featuresForPreview: any[] = [];
  const stakeholderFeaturesForPreview: any[] = [];
  let totalFeatures = 0;
  let totalStakeholderFeatures = 0;
  let skippedUnsupportedFeatures = 0;
  let wgs84Error: string | null = null;
  let discoveredCrs: unknown | undefined;
  let crsChecked = false;
  let parsedAnyFeatureSlot = false;

  const validateCrsIfNeeded = (crs: unknown | undefined) => {
    if (crs === undefined || crsChecked) {
      return;
    }
    discoveredCrs = crs;
    crsChecked = true;
    wgs84Error = applyWgs84Slice(wgs84Error, { crs: discoveredCrs, features: [] });
  };

  const handleFeature = (feature: any) => {
    parsedAnyFeatureSlot = true;
    const geomType = feature?.geometry?.type;
    if (!isAllowedGeoJsonGeometryType(geomType)) {
      skippedUnsupportedFeatures += 1;
      return;
    }
    if (
      !feature.geometry?.coordinates ||
      !Array.isArray(feature.geometry.coordinates) ||
      feature.geometry.coordinates.length === 0
    ) {
      throw new GeoJsonStreamError('geometry.coordinates invalid data');
    }
    if (feature.type !== 'Feature') {
      throw new GeoJsonStreamError('Invalid GeoJSON: expected Feature in features array');
    }
    wgs84Error = applyWgs84Slice(wgs84Error, { features: [feature] });
    totalFeatures += 1;
    if (featuresForPreview.length < previewCap) {
      featuresForPreview.push(feature);
    }
    if (hasPreviewStakeholderName(feature)) {
      totalStakeholderFeatures += 1;
      if (stakeholderFeaturesForPreview.length < previewCap) {
        stakeholderFeaturesForPreview.push(feature);
      }
    }
  };

  if (typeof (file as Blob).stream !== 'function') {
    const text = await file.text();
    return processParsedCollection(JSON.parse(text), previewCap);
  }

  const parser = new JSONParser({
    paths: ['$.crs', '$.features.*'],
    keepStack: false,
  });

  parser.onValue = ({ value, key }) => {
    if (key === 'crs') {
      validateCrsIfNeeded(value);
    } else if (typeof key === 'number') {
      handleFeature(value);
    }
  };

  const reader = file.stream().getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      try {
        parser.end();
      } catch {
        // @streamparser/json may throw when the tokenizer already finished after the last chunk.
      }
      break;
    }
    parser.write(value);
  }

  if (!parsedAnyFeatureSlot) {
    throw new GeoJsonStreamError('Invalid GeoJSON: expected FeatureCollection with features');
  }
  if (totalFeatures === 0) {
    throw new GeoJsonStreamError(
      'FeatureCollection has no supported features (unsupported geometries were skipped)'
    );
  }

  return {
    featuresForPreview,
    stakeholderFeaturesForPreview,
    totalFeatures,
    totalStakeholderFeatures,
    skippedUnsupportedFeatures,
    capped: totalFeatures > previewCap,
    wgs84Error,
  };
}

function processParsedCollection(json: any, previewCap: number): StreamGeoJsonResult {
  if (!json || json.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
    throw new GeoJsonStreamError('Invalid GeoJSON: expected FeatureCollection');
  }
  if (json.features.length === 0) {
    throw new GeoJsonStreamError('FeatureCollection has no features');
  }
  const featuresForPreview: any[] = [];
  const stakeholderFeaturesForPreview: any[] = [];
  let totalFeatures = 0;
  let totalStakeholderFeatures = 0;
  let skippedUnsupportedFeatures = 0;
  let wgs84Error: string | null = null;
  const allowedFeatures: any[] = [];

  json.features.forEach((feature: any) => {
    const geomType = feature?.geometry?.type;
    if (!isAllowedGeoJsonGeometryType(geomType)) {
      skippedUnsupportedFeatures += 1;
      return;
    }
    if (
      !feature.geometry?.coordinates ||
      !Array.isArray(feature.geometry.coordinates) ||
      feature.geometry.coordinates.length === 0
    ) {
      throw new GeoJsonStreamError('geometry.coordinates invalid data');
    }
    allowedFeatures.push(feature);
    totalFeatures += 1;
    if (featuresForPreview.length < previewCap) {
      featuresForPreview.push(feature);
    }
    if (hasPreviewStakeholderName(feature)) {
      totalStakeholderFeatures += 1;
      if (stakeholderFeaturesForPreview.length < previewCap) {
        stakeholderFeaturesForPreview.push(feature);
      }
    }
  });
  if (totalFeatures === 0) {
    throw new GeoJsonStreamError(
      'FeatureCollection has no supported features (unsupported geometries were skipped)'
    );
  }

  wgs84Error = validateWgs84GeoJsonDocument({
    crs: json.crs,
    features: allowedFeatures,
  });

  return {
    featuresForPreview,
    stakeholderFeaturesForPreview,
    totalFeatures,
    totalStakeholderFeatures,
    skippedUnsupportedFeatures,
    capped: totalFeatures > previewCap,
    wgs84Error,
  };
}

/**
 * Build OpenLayers-oriented preview payloads from streamed features
 * (same shapes as legacy select-geo-json-file-modal readFile).
 */
function stakeholderPinFromFeature(
  feature: any,
  calculatePolygonCentroid: (coords: any) => number[]
): { points: { type: string; coordinates: number[][] }; locatable_type: string } | null {
  if (!hasPreviewStakeholderName(feature)) {
    return null;
  }
  const geom = feature.geometry;
  let coordinate: number[] | null = null;
  if (geom.type === 'Polygon') {
    coordinate = calculatePolygonCentroid(geom.coordinates[0]);
  } else if (geom.type === 'MultiPolygon' && geom.coordinates?.[0]?.[0]) {
    coordinate = calculatePolygonCentroid(geom.coordinates[0][0]);
  } else if (geom.type === 'Point') {
    coordinate = geom.coordinates;
  } else if (geom.type === 'MultiPoint' && geom.coordinates?.[0]) {
    coordinate = geom.coordinates[0];
  } else if (geom.type === 'LineString') {
    coordinate = calculatePolygonCentroid(geom.coordinates);
  } else if (geom.type === 'MultiLineString' && geom.coordinates?.[0]) {
    coordinate = calculatePolygonCentroid(geom.coordinates[0]);
  }
  if (!coordinate) {
    return null;
  }
  return {
    points: { type: 'MultiPoint', coordinates: [coordinate] },
    locatable_type: 'stakeholders',
  };
}

export function buildGeoJsonPreviewPayloads(
  features: any[],
  calculatePolygonCentroid: (coords: any) => number[],
  pointToCircleCoords: (pointCoords: any) => any,
  stakeholderFeatures: any[] = features
): { geoPolygons: any[]; stakeholders: any[] } {
  const polygons: any = {
    locatable_type: 'projects',
    metadata: { color: '#3C8CAA' },
  };
  const lines: any = {
    locatable_type: 'projects',
    metadata: { color: '#3C8CAA' },
  };
  const allPolygonCoordinates: any[] = [];
  const allLinesCoordinates: any[] = [];
  const stakeholders: any[] = [];

  features.forEach((feature: any) => {
    const geom = feature.geometry;
    if (geom.type === 'Polygon') {
      allPolygonCoordinates.push(geom.coordinates);
    } else if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
      geom.coordinates.forEach((polygon: any) => allPolygonCoordinates.push(polygon));
    } else if (geom.type === 'Point') {
      allPolygonCoordinates.push(pointToCircleCoords(geom.coordinates));
    } else if (geom.type === 'MultiPoint' && Array.isArray(geom.coordinates)) {
      geom.coordinates.forEach((pt: any) => allPolygonCoordinates.push(pointToCircleCoords(pt)));
    } else if (geom.type === 'LineString') {
      allLinesCoordinates.push(geom.coordinates);
    } else if (geom.type === 'MultiLineString' && Array.isArray(geom.coordinates)) {
      geom.coordinates.forEach((line: any) => allLinesCoordinates.push(line));
    }
  });

  stakeholderFeatures.forEach((feature: any) => {
    const pin = stakeholderPinFromFeature(feature, calculatePolygonCentroid);
    if (pin) {
      stakeholders.push(pin);
    }
  });

  if (allPolygonCoordinates.length) {
    polygons.polygons = { type: 'MultiPolygon', coordinates: allPolygonCoordinates };
  }
  if (allLinesCoordinates.length) {
    lines.lines = { type: 'MultiLineString', coordinates: allLinesCoordinates };
  }
  return { geoPolygons: [polygons, lines], stakeholders };
}
